import React, { useState, useCallback } from "react";
import {
  Badge,
  Popover,
  PopoverHeader,
  PopoverBody,
  UncontrolledTooltip,
  Spinner,
} from "reactstrap";

import { mutate } from "swr";
import { useSelector } from "react-redux";
import { getURLWithParams } from "utils/context";
import { Archive, CheckCircle, Info } from "react-feather";
import { formatCurrency, limitCaracter } from "utils";
import Avatar from "@components/avatar";
import { PopoverInfo } from "components/Structure/PopoverInfo";

import * as S from "./Products.styles";

import FireIcon from "assets/icone-fire.png";
import RiskIcon from "assets/icone-risk.png";
import { handleConfirmAlert } from "components/Structure/ConfirmAlert";
import { useIgnoreProduct } from "useCases";
import { MOVEMENT_TYPE } from "constants/enums";
import { colorStyles } from "constants/Labels/colors";
import { CSTinfo } from "constants/types";

const CST_PIS_COFINS_ALIQUOTA_ZERO = [["04", "06"], ["01"]];
const CST_PIS_COFINS_ALIQUOTA_ZERO_LABEL = [
  {
    label: "Alíquota Básica",
    value: "01",
  },
  {
    label: "Alíquota Zero",
    value: "04",
  },
  {
    label: "Alíquota Zero",
    value: "06",
  },
];

const percente = value =>
  Number(value / 100).toLocaleString(undefined, {
    style: "percent",
    minimumFractionDigits: 2,
  });

const PopoverItems = ({ title, titleCard, id, limit = 35 }) => {
  const [isShowPopover, setIsShowPopover] = useState("");
  const PopoverId = `Popover-${id}`;

  const OpenPopover = () =>
    setIsShowPopover(isShowPopover === PopoverId ? "" : PopoverId);

  return title?.length > 20 ? (
    <>
      <span
        onMouseEnter={() => OpenPopover()}
        onMouseLeave={() => setIsShowPopover("")}
        id={PopoverId}
      >
        {limitCaracter(limit, title)}
      </span>
      <Popover
        isOpen={Boolean(isShowPopover)}
        placement="top"
        toggle={OpenPopover}
        target={PopoverId}
      >
        <PopoverHeader>{titleCard}</PopoverHeader>
        <PopoverBody>{title}</PopoverBody>
      </Popover>
    </>
  ) : (
    title
  );
};

export const BtnIgnore = ({ row, revalidate }) => {
  const { onIgnoreProduct, isLoading } = useIgnoreProduct();
  const { issue_date_start: start, issue_date_end: end } = useSelector(
    state => state.taxAnalises,
  );

  const onSend = useCallback(async () => {
    await onIgnoreProduct({
      xprod: row.xprod,
      cprod: row?.cprod,
      cnpj_empresa: row?.cnpj_empresa,
      ean: row?.cean,
      tipo_movimento: MOVEMENT_TYPE.SAIDA,
    }).then(() => {
      const key = getURLWithParams("/tax-note/diagnostico/totals", {
        issue_date_start: start,
        issue_date_end: end,
        tipo: "XML",
      });
      mutate(key);
      revalidate();
    });
  }, [row, onIgnoreProduct, revalidate, start, end]);

  const onConfirmDelete = useCallback(() => {
    handleConfirmAlert(
      {
        icon: "info",
        title: "Ignorar produto",
        text: "Deseja ignorar esse produto da listagem?",
        textBtn: "Confirmar",
      },
      onSend,
    );
  }, [onSend]);

  return (
    <>
      {isLoading ? (
        <Spinner className="text-danger" />
      ) : (
        <>
          <Avatar
            color="light-danger"
            icon={<Archive size={16} />}
            id={`trash-${row?.key}`}
            onClick={onConfirmDelete}
          />
          <UncontrolledTooltip placement="top" target={`trash-${row?.key}`}>
            Ignorar
          </UncontrolledTooltip>
        </>
      )}
    </>
  );
};

export const getColumns = (revalidate, isUserMix, cprodsCurvaA = new Set()) => [
  {
    name: "",
    width: isUserMix ? "100px" : "55px",
    isType: "queima",
    cell: row => (
      <>
        <div className="d-flex align-items-center gap-1">
          {isUserMix ? <BtnIgnore row={row} revalidate={revalidate} /> : null}
          {cprodsCurvaA.has(row.cprod) && <S.IconeCustom src={FireIcon} />}
        </div>
      </>
    ),
  },
  {
    name: "",
    width: isUserMix ? "100px" : "55px",
    isType: "risco",
    cell: row => (
      <>
        <div className="d-flex align-items-center gap-1">
          {isUserMix ? <BtnIgnore row={row} revalidate={revalidate} /> : null}
          {cprodsCurvaA.has(row.cprod) && <S.IconeCustom src={RiskIcon} />}
        </div>
      </>
    ),
  },
  {
    name: "EAN",
    omit: true,
    sortField: "cean",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "Cod.",
    omit: true,
    sortField: "cprod",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "NCM",
    omit: true,
    sortField: "ncm",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "CEST",
    omit: true,
    sortField: "cest",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "CFOP",
    omit: true,
    sortField: "cfop",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "Produto",
    typesDefaults: [],
    sortField: "xprod",
    isTitle: true,
    minWidth: "300px",
    position: 1,
    cell: row => {
      return (
        <div className="d-flex justify-content-left align-items-center">
          <div className="d-flex flex-column">
            <h5 className="user-name">
              {!row.xprod && (
                <Badge style={{ marginTop: "3px" }}>
                  SEM DESCRIÇÃO DO PRODUTO
                </Badge>
              )}
              {PopoverItems({
                titleCard: "Descrição",
                title: row.xprod,
                limit: 25,
                id: row?.key,
              })}
            </h5>
            <div
              className="user-name"
              style={{
                gridGap: 2,
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                marginBottom: "3px",
              }}
            >
              <Badge color="light-secondary">Cod. Interno: {row.cprod}</Badge>
              {row?.cean && (
                <Badge color="light-secondary">EAN: {row?.cean}</Badge>
              )}
              &nbsp;
              {row?.ncm && <Badge color="light-info">NCM: {row?.ncm}</Badge>}
              &nbsp;
              {row?.cest && (
                <Badge color="light-primary">CEST: {row?.cest}</Badge>
              )}
              &nbsp;
              {row?.cfop && (
                <Badge color="light-secondary">CFOP: {row?.cfop}</Badge>
              )}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    name: "ICMS",
    typesDefaults: [],
    sortField: "queima_caixa_icms",
    isType: "queima",
    position: 1,
    minWidth: "200px",
    grow: 0,
    cell: row => {
      return formatCurrency(row?.queima_caixa_icms);
    },
  },
  {
    name: "PIS/COFINS",
    typesDefaults: [],
    sortField: "queima_caixa_pis_cofins",
    isType: "queima",
    position: 1,
    minWidth: "200px",
    grow: 0,
    cell: row => {
      return formatCurrency(row?.queima_caixa_pis_cofins);
    },
  },
  {
    name: "ICMS",
    typesDefaults: [],
    sortField: "risco_assumido_icms",
    isType: "risco",
    position: 1,
    minWidth: "200px",
    grow: 0,
    cell: row => {
      return formatCurrency(row?.risco_assumido_icms);
    },
  },
  {
    name: "PIS/COFINS",
    typesDefaults: [],
    sortField: "risco_assumido_pis_cofins",
    isType: "risco",
    position: 1,
    minWidth: "200px",
    grow: 0,
    cell: row => {
      return formatCurrency(row?.risco_assumido_pis_cofins);
    },
  },
  {
    name: (
      <span
        className="d-flex align-items-center gap-1"
        style={{ whiteSpace: "nowrap" }}
      >
        <span>ICMS por período</span>
        <PopoverInfo
          placement="top"
          title="ICMS por período"
          isWidthAdpt
          popoverBody={
            <div style={{ width: 320 }}>
              <p className="mb-2">
                <strong>Risco Assumido de ICMS</strong> é o valor de ICMS que
                deixou de ser recolhido por vender produtos com tributação
                incorreta, podendo ser cobrado (com multa e juros) numa
                fiscalização futura.
              </p>
              <p className="mb-1">
                O percentual representa a participação deste produto no risco
                assumido total de ICMS da empresa no período selecionado:
              </p>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  background: "#f3f2f7",
                  borderRadius: 4,
                  padding: "6px 8px",
                }}
              >
                (Risco Assumido ICMS do produto ÷ Risco Assumido ICMS total do
                período) × 100
              </div>
            </div>
          }
        >
          <Info size={12} />
        </PopoverInfo>
      </span>
    ),
    typesDefaults: [],
    isType: "risco",
    typeFormatValue: "percent",
    sortField: "porcentagem_risco_assumido_icms_no_periodo",
    position: 1,
    minWidth: "200px",
    grow: 0,
    cell: row => {
      return `${percente(row?.porcentagem_risco_assumido_icms_no_periodo)}`;
    },
  },
  {
    name: (
      <span
        className="d-flex align-items-center gap-1"
        style={{ whiteSpace: "nowrap" }}
      >
        <span>PIS/COFINS por período</span>
        <PopoverInfo
          placement="top"
          title="PIS/COFINS por período"
          isWidthAdpt
          popoverBody={
            <div style={{ width: 320 }}>
              <p className="mb-2">
                <strong>Risco Assumido de PIS/COFINS</strong> é o valor de
                PIS/COFINS que deixou de ser recolhido por vender produtos com
                tributação incorreta, podendo ser cobrado (com multa e juros)
                numa fiscalização futura.
              </p>
              <p className="mb-1">
                O percentual representa a participação deste produto no risco
                assumido total de PIS/COFINS da empresa no período selecionado:
              </p>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  background: "#f3f2f7",
                  borderRadius: 4,
                  padding: "6px 8px",
                }}
              >
                (Risco Assumido PIS/COFINS do produto ÷ Risco Assumido
                PIS/COFINS total do período) × 100
              </div>
            </div>
          }
        >
          <Info size={12} />
        </PopoverInfo>
      </span>
    ),
    typesDefaults: [],
    isType: "risco",
    typeFormatValue: "percent",
    sortField: "porcentagem_risco_assumido_pis_cofins_no_periodo",
    position: 1,
    minWidth: "230px",
    grow: 0,
    cell: row => {
      return `${percente(
        row?.porcentagem_risco_assumido_pis_cofins_no_periodo,
      )}`;
    },
  },
  {
    name: "Valor do Produto",
    typesDefaults: [],
    sortField: "vprod",
    position: 1,
    typeFormatValue: "monay",
    omit: true,
    cell: row => {
      return formatCurrency(row?.vprod);
    },
  },
  {
    name: (
      <span
        className="d-flex align-items-center gap-1"
        style={{ whiteSpace: "nowrap" }}
      >
        <span>ICMS por período</span>
        <PopoverInfo
          placement="top"
          title="Queima de Caixa de ICMS por período"
          isWidthAdpt
          popoverBody={
            <div style={{ width: 320 }}>
              <p className="mb-2">
                <strong>Queima de Caixa de ICMS</strong> é o valor de ICMS pago
                a mais do que o necessário por vender produtos com tributação
                incorreta.
              </p>
              <p className="mb-1">
                O percentual representa a participação deste produto na queima
                de caixa total de ICMS da empresa no período selecionado:
              </p>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  background: "#f3f2f7",
                  borderRadius: 4,
                  padding: "6px 8px",
                }}
              >
                (Queima de Caixa ICMS do produto ÷ Queima de Caixa ICMS total do
                período) × 100
              </div>
            </div>
          }
        >
          <Info size={12} />
        </PopoverInfo>
      </span>
    ),
    typesDefaults: [],
    isType: "queima",
    sortField: "porcentagem_queima_caixa_icms_no_periodo",
    typeFormatValue: "percent",
    position: 1,
    minWidth: "200px",
    grow: 0,
    cell: row => {
      return `${percente(row?.porcentagem_queima_caixa_icms_no_periodo)}`;
    },
  },
  {
    name: (
      <span
        className="d-flex align-items-center gap-1"
        style={{ whiteSpace: "nowrap" }}
      >
        <span>PIS/COFINS por período</span>
        <PopoverInfo
          placement="top"
          title="Queima de Caixa de PIS/COFINS por período"
          isWidthAdpt
          popoverBody={
            <div style={{ width: 320 }}>
              <p className="mb-2">
                <strong>Queima de Caixa de PIS/COFINS</strong> é o valor de
                PIS/COFINS pago a mais do que o necessário por vender produtos
                com tributação incorreta.
              </p>
              <p className="mb-1">
                O percentual representa a participação deste produto na queima
                de caixa total de PIS/COFINS da empresa no período selecionado:
              </p>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  background: "#f3f2f7",
                  borderRadius: 4,
                  padding: "6px 8px",
                }}
              >
                (Queima de Caixa PIS/COFINS do produto ÷ Queima de Caixa
                PIS/COFINS total do período) × 100
              </div>
            </div>
          }
        >
          <Info size={12} />
        </PopoverInfo>
      </span>
    ),
    typesDefaults: [],
    isType: "queima",
    sortField: "porcentagem_queima_caixa_pis_cofins_no_periodo",
    typeFormatValue: "percent",
    position: 1,
    minWidth: "230px",
    grow: 0,
    cell: row => {
      return `${percente(row?.porcentagem_queima_caixa_pis_cofins_no_periodo)}`;
    },
  },
  {
    name: (
      <span
        className="d-flex align-items-center gap-1"
        style={{ whiteSpace: "nowrap" }}
      >
        <span>CST ICMS</span>
        <PopoverInfo
          placement="top"
          title="Cliente x Mix"
          isWidthAdpt
          popoverBody={
            <div className="d-flex flex-column gap-2" style={{ width: 320 }}>
              <div className="d-flex align-items-start gap-1">
                <span
                  className="bullet bullet-warning"
                  style={{ width: 14, height: 8, marginTop: 6, flexShrink: 0 }}
                />
                <span>
                  <strong>Tributado Integralmente:</strong> o ICMS incide sobre
                  100% do valor da operação, sem redução, isenção ou
                  substituição — o próprio vendedor calcula e recolhe o imposto
                  da sua venda.
                </span>
              </div>
              <div className="d-flex align-items-start gap-1">
                <span
                  className="bullet bullet-info"
                  style={{ width: 14, height: 8, marginTop: 6, flexShrink: 0 }}
                />
                <span>
                  <strong>Substituição Tributária:</strong> um único elo da
                  cadeia (geralmente o fabricante/importador) já recolheu o ICMS
                  de toda a cadeia, incluindo as etapas seguintes até o
                  consumidor final.
                </span>
              </div>
            </div>
          }
        >
          <Info size={12} />
        </PopoverInfo>
      </span>
    ),
    typesDefaults: [],
    sortField: "cst_icms",
    position: 1,
    minWidth: "280px",
    grow: 0,
    cell: row => {
      const icmsBase = row?.cst_icms ?? "-";
      const icmsMix = row?.cst_icms_mix ?? "-";
      const icmsBaseLabel = CSTinfo.find(cst => cst.value === icmsBase)?.label;
      const icmsMixLabel = CSTinfo.find(cst => cst.value === icmsMix)?.label;

      if (icmsBase === icmsMix) {
        return (
          <div
            className="d-grid align-items-center gap-1"
            style={{
              gridTemplateColumns: "56px 40px 150px",
              whiteSpace: "nowrap",
            }}
          >
            <span />
            <span
              style={{
                background: colorStyles.success.light,
                color: colorStyles.success.solid,
                borderRadius: "0.358rem",
                padding: "1px 6px",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              {icmsBase}
            </span>
            <span
              className="d-flex align-items-center gap-1"
              style={{ color: colorStyles.success.solid }}
            >
              <CheckCircle size={14} color={colorStyles.success.solid} />
              <span style={{ fontSize: 10 }}>Sem divergência</span>
            </span>
          </div>
        );
      }

      return (
        <div
          className="d-grid align-items-center gap-1"
          style={{
            gridTemplateColumns: "56px 40px 150px",
            whiteSpace: "nowrap",
          }}
        >
          <span>Cliente</span>
          <span
            style={{
              background: colorStyles.warning.light,
              color: colorStyles.warning.solid,
              borderRadius: "0.358rem",
              padding: "1px 6px",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            {icmsBase}
          </span>
          <span style={{ fontWeight: 500, fontSize: 10 }}>{icmsBaseLabel}</span>
          <span>Mix</span>
          <span
            style={{
              background: colorStyles.info.light,
              color: colorStyles.info.solid,
              borderRadius: "0.358rem",
              padding: "1px 6px",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            {icmsMix}
          </span>
          <span style={{ fontWeight: 500, fontSize: 10 }}>{icmsMixLabel}</span>
        </div>
      );
    },
  },
  {
    name: (
      <span
        className="d-flex align-items-center gap-1"
        style={{ whiteSpace: "nowrap" }}
      >
        <span>CST PIS/COFINS</span>
        <PopoverInfo
          placement="top"
          title="Cliente x Mix"
          isWidthAdpt
          popoverBody={
            <div className="d-flex flex-column gap-2" style={{ width: 320 }}>
              <div className="d-flex align-items-start gap-1">
                <span
                  className="bullet bullet-warning"
                  style={{ width: 14, height: 8, marginTop: 6, flexShrink: 0 }}
                />
                <span>
                  <strong>Alíquota Zero:</strong> tributável, mas com alíquota
                  reduzida a 0% por benefício legal (ex.: cesta básica, produtos
                  monofásicos). Diferente de isenção ou não incidência — a base
                  de cálculo existe, só o percentual é zero.
                </span>
              </div>
              <div className="d-flex align-items-start gap-1">
                <span
                  className="bullet bullet-info"
                  style={{ width: 14, height: 8, marginTop: 6, flexShrink: 0 }}
                />
                <span>
                  <strong>Alíquota Básica:</strong> regra geral de tributação —
                  alíquotas padrão do regime não cumulativo (1,65% PIS / 7,6%
                  COFINS) sobre a receita bruta, sem benefício ou redução.
                </span>
              </div>
              <div className="d-flex align-items-start gap-1">
                <span
                  className="bullet bullet-success"
                  style={{ width: 14, height: 8, marginTop: 6, flexShrink: 0 }}
                />
                <span>Impostos sem divergência</span>
              </div>
            </div>
          }
        >
          <Info size={12} />
        </PopoverInfo>
      </span>
    ),
    typesDefaults: [],
    sortField: "cst_pis",
    position: 1,
    minWidth: "280px",
    grow: 0,
    cell: row => {
      const pisBase = row?.cst_pis || "-";
      const pisMix = row?.cst_pis_mix || "-";
      const pisBaseLabel = CST_PIS_COFINS_ALIQUOTA_ZERO_LABEL.find(
        cst => cst.value === pisBase,
      )?.label;
      const pisMixLabel = CST_PIS_COFINS_ALIQUOTA_ZERO_LABEL.find(
        cst => cst.value === pisMix,
      )?.label;

      const matchedGroup = CST_PIS_COFINS_ALIQUOTA_ZERO.find(
        group => group.includes(pisBase) && group.includes(pisMix),
      );

      if (matchedGroup) {
        const label = matchedGroup.includes("01")
          ? "Alíquota Básica"
          : "Alíquota Zero";

        return (
          <div
            className="d-grid align-items-center gap-1"
            style={{
              gridTemplateColumns: "56px 40px 150px",
              whiteSpace: "nowrap",
              paddingLeft: "24px",
            }}
          >
            <span />
            <span
              style={{
                background: colorStyles.success.light,
                color: colorStyles.success.solid,
                borderRadius: 4,
                padding: "1px 6px",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              {pisBase}
            </span>
            <span
              className="d-flex align-items-center gap-1"
              style={{ color: colorStyles.success.solid }}
            >
              <CheckCircle size={14} color={colorStyles.success.solid} />
              <span style={{ fontSize: 10 }}>{label}</span>
            </span>
          </div>
        );
      }

      return (
        <div
          className="d-grid align-items-center gap-1"
          style={{
            gridTemplateColumns: "56px 40px 150px",
            whiteSpace: "nowrap",
            paddingLeft: "24px",
          }}
        >
          <span>Cliente</span>
          <span
            style={{
              background: colorStyles.warning.light,
              color: colorStyles.warning.solid,
              borderRadius: 4,
              padding: "1px 6px",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            {pisBase}
          </span>
          <span style={{ fontWeight: 500, fontSize: 10 }}>{pisBaseLabel}</span>
          <span>Mix</span>
          <span
            style={{
              background: colorStyles.info.light,
              color: colorStyles.info.solid,
              borderRadius: 4,
              padding: "1px 6px",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            {pisMix}
          </span>
          <span style={{ fontWeight: 500, fontSize: 10 }}>{pisMixLabel}</span>
        </div>
      );
    },
  },
];
